import React from "react";
import { createRoot } from "react-dom/client";
import { useFieldArray, useForm } from "react-hook-form";
import { validatePhoneNumber } from "./phone_validation";
import {
  AlertFillIcon,
  CheckCircleIcon,
  IssueDraftIcon,
  PaperAirplaneIcon,
} from "@primer/octicons-react";

const h = React.createElement;
const maxDocumentFileSize = 5 * 1024 * 1024;
const maxDocumentsPerField = 10;

export const priorityOptions = [
  { label: "high priority", value: "high" },
  { label: "moderate", value: "moderate" },
  { label: "low priority", value: "low" },
  { label: "not applicable", value: "n/a" },
];

const submissionStage = {
  title: "Representations, Declarations, and Signature",
  icon: PaperAirplaneIcon,
  submission: true,
};

const otpAttemptField = {
  label: "6-digit code from authenticator",
  name: "otp_attempt",
  type: "string",
  required: true,
  autocomplete: "one-time-code",
};

const booleanRadiobuttonOptions = [
  { label: "yes", value: "true" },
  { label: "no", value: "false" },
];

let stages = [];
let requiredGroups = [];
let booleanRadiobuttonFieldNames = new Set();
let customNormalizeFieldValue = null;
let customNormalizeFormPayload = null;
let customFieldChange = null;

function defaultValues() {
  return stages.reduce((values, stage) => {
    stage.fields.forEach((field) => {
      if (!field.name) return;

      if (field.type === "array" && field.initialValue) {
        setPath(values, field.name, field.initialValue.map((item) => ({ ...item })));
      } else if (field.type === "array" || field.type === "documents") {
        setPath(values, field.name, []);
      } else if (field.type === "checkbox") setPath(values, field.name, false);
      else setPath(values, field.name, "");
    });

    return values;
  }, {});
}

function csrfToken() {
  return document
    .querySelector("meta[name='csrf-token']")
    ?.getAttribute("content");
}

function stageBody(values, names) {
  return names.reduce((body, name) => {
    setPath(body, name, normalizeFieldValue(name, getPath(values, name), values));
    return body;
  }, {});
}

function normalizeFieldValue(name, value, values) {
  const customValue = customNormalizeFieldValue?.(name, value, values);
  if (customValue?.handled) return customValue.value;

  if (booleanRadiobuttonFieldNames.has(name)) {
    if (value === "true") return true;
    if (value === "false") return false;
  }

  return value;
}

function normalizeFormPayload(payload) {
  const formPayload = deepMerge(payload);

  booleanRadiobuttonFieldNames.forEach((name) => {
    const value = getPath(formPayload, name);

    if (value === true) setPath(formPayload, name, "true");
    if (value === false) setPath(formPayload, name, "false");
  });

  return customNormalizeFormPayload?.(formPayload) || formPayload;
}

function payloadFromSections(sections) {
  return Object.entries(sections || {}).reduce((payload, [name, section]) => {
    payload[name] = section?.payload || {};
    return payload;
  }, {});
}

function lockVersionsFromSections(sections) {
  return Object.entries(sections || {}).reduce(
    (lockVersions, [name, section]) => {
      if (
        section &&
        Object.prototype.hasOwnProperty.call(section, "lock_version")
      ) {
        lockVersions[name] = section.lock_version;
      }

      return lockVersions;
    },
    {},
  );
}

function stageSectionsPayload(values, lockVersions, stage) {
  const payload = trimStringPayload(stageBody(values, stageNames(stage)));

  return Object.entries(payload).reduce(
    (sectionsPayload, [name, sectionPayload]) => {
      sectionsPayload[name] = { payload: sectionPayload };

      if (Object.prototype.hasOwnProperty.call(lockVersions, name)) {
        sectionsPayload[name].lock_version = lockVersions[name];
      }

      return sectionsPayload;
    },
    {},
  );
}

function getPath(object, path) {
  return path.split(".").reduce((value, key) => value?.[key], object);
}

function hasPath(object, path) {
  let value = object;

  return path.split(".").every((key) => {
    if (!value || !Object.prototype.hasOwnProperty.call(value, key)) {
      return false;
    }

    value = value[key];
    return true;
  });
}

function setPath(object, path, value) {
  const keys = path.split(".");
  const lastKey = keys.pop();
  const target = keys.reduce((memo, key) => {
    memo[key] ||= {};
    return memo[key];
  }, object);

  target[lastKey] = value;
}

function deepMerge(...objects) {
  return objects.reduce((result, object) => {
    Object.entries(object || {}).forEach(([key, value]) => {
      if (isPlainObject(value) && isPlainObject(result[key])) {
        result[key] = deepMerge(result[key], value);
      } else {
        result[key] = value;
      }
    });

    return result;
  }, {});
}

function isPlainObject(value) {
  return value && typeof value === "object" && !Array.isArray(value);
}

function samePayload(left, right) {
  return JSON.stringify(left) === JSON.stringify(right);
}

function stageIsComplete(stage, values, serverPayload) {
  const visibleNames = stageValidationNames(stage, values);
  const names = stageNames(stage);

  return (
    visibleNames.every((name) => hasPath(serverPayload, name)) &&
    stageIsValid(stage, values) &&
    samePayload(
      stageBody(values, names),
      stageBody(serverPayload, names),
    )
  );
}

function stageIsValid(stage, values) {
  return (
    stage.fields.every((field) =>
      field.type === "paragraph"
        ? true
        : fieldIsValid(field, getPath(values, field.name), values),
    ) && requiredGroupsAreValid(stage, values)
  );
}

function requiredGroupsAreValid(stage, values) {
  return requiredGroups.every(
    (group) =>
      !stageIncludesAllNames(stage, group) ||
      group.some((fieldName) => getPath(values, fieldName) === true),
  );
}

function stageIncludesAllNames(stage, names) {
  const fieldNames = stageNames(stage);

  return names.every((fieldName) => fieldNames.includes(fieldName));
}

function stageValidationNames(stage, values) {
  return stageNames(stage).filter((name) => {
    const field = stage.fields.find((item) => item.name === name);

    return !field || fieldIsShown(field, values);
  });
}

function stageNames(stage) {
  return (stage.fields || []).flatMap((field) =>
    field.name ? [field.name] : [],
  );
}

function responseErrorMessage(status) {
  if (status === 401)
    return "Your session has expired—please reload the page to sign in again";
  if (status === 403)
    return "Form can't be edited as it was signed already—please reload the page to see the changes";
  if (status === 409)
    return "Form can't be submitted as someone updated it—please reload the page to see the changes";
  if (status === 422)
    return "Form submission failed—please reload the page and try again for incomplete sections";
  if (status === 428)
    return "Form can't be submitted because you have to pass verification—please reload the page to see the changes";
  if (status === 500)
    return "Something went wrong. We've been notified about this error and will look at it shortly.";
  if (status === 503)
    return "The application is temporarily unavailable while we perform maintenance. Please try again later.";

  return `Request failed with ${status}`;
}

function fieldIsValid(field, value, values) {
  if (!fieldIsShown(field, values)) return true;

  if (field.type === "paragraph") return true;

  if (field.type === "array") {
    const items = value || [];

    return (
      (!field.requiredWhenShown || items.length > 0) &&
      items.every((item) =>
        field.fields.every((subfield) =>
          fieldIsValid(subfield, getPath(item, subfield.name), item),
        ),
      )
    );
  }

  if (field.type === "documents") return !field.required || value?.length > 0;

  if (!field.required) return true;

  if (field.type === "checkbox") return value === true;

  if (field.type === "number") {
    return (
      Number.isInteger(value) &&
      (field.min === undefined || value >= field.min)
    );
  }

  return value !== undefined && value !== null && String(value).trim() !== "";
}

function Field({ field, register, errors, onChange, values }) {
  if (!fieldIsShown(field, values)) return null;

  if (field.type === "paragraph") {
    return h("p", { style: styles.paragraph }, field.text);
  }

  const error = getPath(errors, field.name);

  if (field.type === "array") return null;

  if (field.type === "checkbox") {
    return h(
      "div",
      { style: styles.field },
      h(
        "label",
        { style: styles.checkboxOption },
        renderInput(field, register, "", onChange),
        h("span", null, field.label),
      ),
      field.paragraph && h("small", { style: styles.help }, field.paragraph),
      errorMessage(error),
    );
  }

  return h(
    "label",
    { style: styles.field },
    h("span", { style: styles.label }, field.label),
    field.paragraph && h("small", { style: styles.help }, field.paragraph),
    renderInput(field, register, "", onChange),
    errorMessage(error),
  );
}

function renderInput(field, register, namePrefix = "", onChange) {
  const name = `${namePrefix}${field.name}`;
  const options = registrationOptions(field);
  const registration = register(name, options);
  const handleChange = (event) => {
    if (field.type === "number") {
      event.currentTarget.value = formatNumberInputValue(
        event.currentTarget.value,
      );
    }

    registration.onChange(event);
    onChange?.(name);
  };

  if (field.type === "textarea") {
    return h("textarea", {
      ...registration,
      onInput: autosizeTextarea,
      onChange: (event) => {
        handleChange(event);
        autosizeTextarea(event);
      },
      rows: 1,
      style: styles.textarea,
    });
  }

  if (field.type === "select") {
    return h(
      "select",
      {
        ...registration,
        onChange: handleChange,
        style: styles.input,
      },
      h("option", { value: "" }, ""),
      field.options.map((option) =>
        h("option", { key: option.value, value: option.value }, option.label),
      ),
    );
  }

  if (field.type === "checkbox") {
    return h("input", {
      ...registration,
      onChange: handleChange,
      type: "checkbox",
      style: styles.choiceInput,
    });
  }

  if (["radiobutton", "boolean_radiobutton"].includes(field.type)) {
    const options = field.type === "boolean_radiobutton" ? booleanRadiobuttonOptions : field.options;

    return h(
      "div",
      { style: styles.radioGroup },
      options.map((option) =>
        h(
          "label",
          { key: option.value, style: styles.radioOption },
          h("input", {
            ...registration,
            onChange: handleChange,
            type: "radio",
            value: option.value,
            style: styles.radioInput,
          }),
          h("span", null, option.label),
        ),
      ),
    );
  }

  return h("input", {
    ...registration,
    ref: (element) => {
      registration.ref(element);

      if (field.type === "number" && element) {
        element.value = formatNumberInputValue(element.value);
      }
    },
    onChange: handleChange,
    type:
      field.type === "date" ? "date" : field.type === "phone" ? "tel" : "text",
    inputMode: field.type === "number" ? "numeric" : undefined,
    autoComplete: field.autocomplete,
    min: field.type === "number" ? field.min : undefined,
    pattern: field.type === "number" ? "[0-9,]*" : undefined,
    style: styles.input,
  });
}

function numberInputDigits(value) {
  return String(value || "").replace(/\D/g, "");
}

function formatNumberInputValue(value) {
  return numberInputDigits(value).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

function registrationOptions(field) {
  const options = field.required ? { required: "This field is required" } : {};

  if (["string", "textarea"].includes(field.type)) {
    options.validate = (value) =>
      !field.required ||
      String(value || "").trim() !== "" ||
      "This field is required";
  }

  if (field.type === "phone") {
    options.validate = (value) => validatePhoneNumber(value, field.required);
  }

  if (field.type === "number") {
    options.setValueAs = (value) => {
      const digits = numberInputDigits(value);

      if (digits === "") return "";

      return Number(digits);
    };
    options.validate = (value) => {
      if (value === "") return !field.required || "This field is required";
      if (!Number.isInteger(value)) return "Enter a whole number";
      if (field.min !== undefined && value < field.min) {
        return `Minimum value is ${formatNumberInputValue(field.min)}`;
      }

      return true;
    };
  }

  return options;
}

function trimStringPayload(value) {
  if (typeof value === "string") return value.trim();

  if (Array.isArray(value)) return value.map((item) => trimStringPayload(item));

  if (isPlainObject(value)) {
    return Object.entries(value).reduce(
      (payload, [key, item]) => ({
        ...payload,
        [key]: trimStringPayload(item),
      }),
      {},
    );
  }

  return value;
}

function documentFileName(document) {
  return (
    document.file_filename || document.name || document.filename || "Document"
  );
}

function autosizeTextarea(event) {
  const textarea = event.currentTarget;

  textarea.style.height = "auto";
  textarea.style.height = `${textarea.scrollHeight}px`;
}

function autosizeTextareas(root = document) {
  root.querySelectorAll("textarea").forEach((textarea) => {
    textarea.style.height = "auto";
    textarea.style.height = `${textarea.scrollHeight}px`;
  });
}

function errorMessage(error, message = "This field is required") {
  return (
    error &&
    h(
      "small",
      { role: "alert", style: styles.error },
      h(AlertFillIcon, { size: 16, style: styles.errorIcon }),
      h("span", null, error.message || message),
    )
  );
}

function validateStageDocuments(stage, values, setError, clearErrors) {
  return stage.fields.reduce((valid, field) => {
    if (
      field.type !== "documents" ||
      !field.required ||
      !fieldIsShown(field, values)
    ) return valid;

    const documents = getPath(values, field.name);
    if (Array.isArray(documents) && documents.length > 0) {
      clearErrors(field.name);
      return valid;
    }

    setError(field.name, {
      type: "required",
      message: "This field is required",
    });

    return false;
  }, true);
}

function validateRequiredGroups(
  stage,
  values,
  setError,
  clearErrors,
) {
  return requiredGroups.reduce((valid, group) => {
    if (!stageIncludesAllNames(stage, group)) return valid;

    return validateRequiredGroup(group, values, setError, clearErrors)
      ? valid
      : false;
  }, true);
}

function validateRequiredGroup(group, values, setError, clearErrors) {
  if (group.some((fieldName) => getPath(values, fieldName) === true)) {
    clearErrors(group);
    return true;
  }

  group.forEach((fieldName) => {
    setError(fieldName, {
      type: "required",
      message: "At least one of these fields is required",
    });
  });

  return false;
}

function requiredGroupForField(stage, name) {
  return requiredGroups.find(
    (group) => group.includes(name) && stageIncludesAllNames(stage, group),
  );
}

function DocumentsField({
  clearErrors,
  documentUrl,
  errors,
  field,
  register,
  setError,
  setValue,
  values,
}) {
  const [uploading, setUploading] = React.useState(false);
  const [uploadError, setUploadError] = React.useState("");
  const shown = fieldIsShown(field, values);
  const documents = getPath(values, field.name) || [];
  const error = getPath(errors, field.name);

  React.useEffect(() => {
    if (!shown) {
      clearErrors(field.name);
      return;
    }

    register(field.name, {
      validate: (value) =>
        !field.required || value?.length > 0 || "This field is required",
    });
  }, [field.name, field.required, register, shown, clearErrors]);

  if (!shown) return null;

  const updateDocuments = (nextDocuments) => {
    setValue(field.name, nextDocuments, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });

    if (field.required && nextDocuments.length === 0) {
      setError(field.name, {
        type: "required",
        message: "This field is required",
      });
    } else {
      clearErrors(field.name);
    }
  };

  const uploadFiles = async (event) => {
    const files = Array.from(event.currentTarget.files || []);
    event.currentTarget.value = "";

    if (files.length === 0) return;

    if (documents.length + files.length > maxDocumentsPerField) {
      setUploadError(
        `A maximum of ${maxDocumentsPerField} documents is allowed`,
      );
      return;
    }

    const oversizedFile = files.find(
      (file) => file.size > maxDocumentFileSize,
    );

    if (oversizedFile) {
      setUploadError(`${oversizedFile.name} exceeds the 5 MB limit`);
      return;
    }

    setUploading(true);
    setUploadError("");

    try {
      const uploadedDocuments = [];

      for (const file of files) {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("name", file.name);

        const token = csrfToken();
        const response = await fetch(documentUrl, {
          method: "POST",
          credentials: "same-origin",
          headers: {
            Accept: "application/json",
            ...(token ? { "X-CSRF-Token": token } : {}),
          },
          body: formData,
        });

        if (!response.ok) throw new Error(responseErrorMessage(response.status));

        uploadedDocuments.push(await response.json());
      }

      updateDocuments([...documents, ...uploadedDocuments]);
    } catch (error) {
      setUploadError(error instanceof Error ? error.message : "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return h(
    "div",
    { style: styles.field },
    h("span", { style: styles.label }, field.label),
    field.paragraph && h("small", { style: styles.help }, field.paragraph),
    documents.length > 0 &&
      h(
        "ul",
        { style: styles.documentsList },
        documents.map((document, index) =>
          h(
            "li",
            { key: document.id || `${documentFileName(document)}-${index}` },
            h("span", null, documentFileName(document)),
            h(
              "a",
              {
                style: styles.documentRemove,
                onClick: () =>
                  updateDocuments(documents.filter((_, i) => i !== index)),
              },
              "Remove",
            ),
          ),
        ),
      ),
    h("input", {
      accept: "application/pdf",
      disabled:
        uploading || !documentUrl || documents.length >= maxDocumentsPerField,
      multiple: true,
      onChange: uploadFiles,
      type: "file",
    }),
    uploading && h("small", { style: styles.help }, "Uploading..."),
    errorMessage(!documentUrl, "Document upload URL is missing"),
    errorMessage(uploadError, uploadError),
    errorMessage(error),
  );
}

function fieldIsShown(field, values) {
  if (!field.showWhen) return true;

  const conditions = Array.isArray(field.showWhen)
    ? field.showWhen
    : [field.showWhen];

  return conditions.every((condition) => {
    const value = getPath(values, condition.name);

    if (
      condition.present &&
      (value === undefined || value === null || value === "")
    ) {
      return false;
    }
    if (Object.prototype.hasOwnProperty.call(condition, "notValue")) {
      return String(value) !== String(condition.notValue);
    }

    return String(value) === String(condition.value);
  });
}

function ArrayField({
  clearErrors,
  documentUrl,
  field,
  control,
  errors,
  onChange,
  register,
  setError,
  setValue,
  values,
}) {
  const { fields, append, remove } = useFieldArray({
    control,
    name: field.name,
  });
  const shown = fieldIsShown(field, values);

  if (!shown) return null;

  return h(
    "div",
    { style: styles.arrayField },
    field.paragraph && h("small", { style: styles.help }, field.paragraph),
    fields.map((item, index) =>
      h(
        "section",
        { key: item.id, style: styles.arrayItem },
        !(field.keepFirstWhenShown && index === 0) &&
          h(
            "button",
            {
              type: "button",
              onClick: () => remove(index),
              className: "btn_secondary",
              "aria-label": "Remove item",
              style: styles.arrayRemoveButton,
            },
            "Remove",
          ),
        field.fields.map((subfield) => {
          const error = getPath(
            errors,
            `${field.name}.${index}.${subfield.name}`,
          );

          if (subfield.type === "documents") {
            return h(DocumentsField, {
              key: subfield.name,
              clearErrors,
              documentUrl,
              errors,
              field: {
                ...subfield,
                name: `${field.name}.${index}.${subfield.name}`,
              },
              register,
              setError,
              setValue,
              values,
            });
          }

          return subfield.type === "checkbox"
            ? h(
                "div",
                { key: subfield.name, style: styles.field },
                h(
                  "label",
                  { style: styles.checkboxOption },
                  renderInput(
                    subfield,
                    register,
                    `${field.name}.${index}.`,
                    onChange,
                  ),
                  h("span", null, subfield.label),
                ),
                errorMessage(error),
              )
            : h(
                "label",
                { key: subfield.name, style: styles.field },
                h("span", { style: styles.label }, subfield.label),
                renderInput(
                  subfield,
                  register,
                  `${field.name}.${index}.`,
                  onChange,
                ),
                errorMessage(error),
              );
        }),
      ),
    ),
    h(
      "button",
      {
        type: "button",
        onClick: () => append({ ...field.defaultValue }),
        className: "btn_secondary",
        style: styles.arrayAddButton,
      },
      field.addLabel,
    ),
  );
}

function OnboardingForm({ documentUrl, url }) {
  const [stageIndex, setStageIndex] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [submitting, setSubmitting] = React.useState(false);
  const [initHtml, setInitHtml] = React.useState("");
  const [submitError, setSubmitError] = React.useState("");
  const [serverPayload, setServerPayload] = React.useState({});
  const [lockVersions, setLockVersions] = React.useState({});
  const [submitUrl, setSubmitUrl] = React.useState("");
  const [totalLockVersion, setTotalLockVersion] = React.useState(null);
  const [comment, setComment] = React.useState("");
  const [commentedAt, setCommentedAt] = React.useState("");
  const [canSign, setCanSign] = React.useState(false);
  const [canSignMessage, setCanSignMessage] = React.useState("");
  const [representations, setRepresentations] = React.useState("");
  const [readyToSign, setReadyToSign] = React.useState(false);
  const [readyToSignMessages, setReadyToSignMessages] = React.useState([]);
  const visibleStages = [...stages, submissionStage];
  const stage = visibleStages[Math.min(stageIndex, visibleStages.length - 1)];
  const {
    control,
    clearErrors,
    formState: { errors },
    getValues,
    register,
    reset,
    setError,
    setValue,
    trigger,
    watch,
  } = useForm({ defaultValues: defaultValues() });
  const values = watch();

  React.useEffect(() => {
    const controller = new AbortController();

    async function loadPayload() {
      setLoading(true);
      setSubmitError("");

      try {
        const response = await fetch(url, {
          method: "GET",
          credentials: "same-origin",
          headers: { Accept: "application/json" },
          signal: controller.signal,
        });

        if (!response.ok) throw new Error(responseErrorMessage(response.status));

        const responseBody = await response.json();
        const sections = responseBody.sections || {};
        const payload = responseBody.payload || payloadFromSections(sections);
        const formPayload = normalizeFormPayload(payload);

        setInitHtml(responseBody.init_html || "");
        setServerPayload(payload);
        setLockVersions(lockVersionsFromSections(sections));
        setSubmitUrl(responseBody.submit_url || "");
        setTotalLockVersion(responseBody.total_lock_version);
        setComment(responseBody.comment || "");
        setCommentedAt(responseBody.commented_at || "");
        setCanSign(responseBody.can_sign === true);
        setCanSignMessage(responseBody.can_sign_message || "");
        setRepresentations(responseBody.representations || "");
        setReadyToSign(responseBody.ready_to_sign === true);
        setReadyToSignMessages(responseBody.ready_to_sign_messages || []);
        const nextPayload = deepMerge(defaultValues(), formPayload);
        reset(nextPayload);
        requestAnimationFrame(() => autosizeTextareas());
      } catch (error) {
        if (!(error instanceof DOMException && error.name === "AbortError")) {
          setSubmitError(
            error instanceof Error ? error.message : "Request failed",
          );
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }

    loadPayload();

    return () => controller.abort();
  }, [url, reset]);

  const submitStage = async (values) => {
    setSubmitting(true);
    setSubmitError("");

    try {
      const token = csrfToken();
      const response = await fetch(url, {
        method: "PATCH",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          ...(token ? { "X-CSRF-Token": token } : {}),
        },
        body: JSON.stringify(stageSectionsPayload(values, lockVersions, stage)),
      });

      if (response.status === 401) {
        window.location.reload();
        return;
      }

      if (!response.ok) throw new Error(responseErrorMessage(response.status));

      const responseBody = await response.json();
      const sections = responseBody.sections || {};
      const payload = responseBody.payload || payloadFromSections(sections);
      const formPayload = normalizeFormPayload(payload);

      setServerPayload(payload);
      setLockVersions(lockVersionsFromSections(sections));
      setSubmitUrl(responseBody.submit_url || "");
      setTotalLockVersion(responseBody.total_lock_version);
      setComment(responseBody.comment || "");
      setCommentedAt(responseBody.commented_at || "");
      setCanSign(responseBody.can_sign === true);
      setCanSignMessage(responseBody.can_sign_message || "");
      setRepresentations(responseBody.representations || "");
      setReadyToSign(responseBody.ready_to_sign === true);
      setReadyToSignMessages(responseBody.ready_to_sign_messages || []);
      const nextPayload = deepMerge(defaultValues(), formPayload);
      reset(nextPayload);
      requestAnimationFrame(() => autosizeTextareas());

      if (stageIndex < visibleStages.length - 1) setStageIndex(stageIndex + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : "Request failed");
    } finally {
      setSubmitting(false);
    }
  };

  const submitOnboarding = async (values) => {
    setSubmitting(true);
    setSubmitError("");

    try {
      const token = csrfToken();
      const response = await fetch(submitUrl, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          ...(token ? { "X-CSRF-Token": token } : {}),
        },
        body: JSON.stringify({
          total_lock_version: totalLockVersion,
          otp_attempt: trimStringPayload(values.otp_attempt),
        }),
      });

      if (response.status === 401) {
        window.location.reload();
        return;
      }

      if (!response.ok) throw new Error(responseErrorMessage(response.status));

      const responseBody = await response.json();
      if (responseBody.errors?.otp_attempt) {
        setError("otp_attempt", { message: responseBody.errors.otp_attempt });
        return;
      }

      if (responseBody.redirect_url) window.location.href = responseBody.redirect_url;
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : "Request failed");
    } finally {
      setValue("otp_attempt", "");
      setSubmitting(false);
    }
  };

  const handleStageSubmit = async (event) => {
    event.preventDefault();

    const values = getValues();

    if (stage.submission) {
      if (canSign && !(await trigger("otp_attempt"))) return;

      await submitOnboarding(values);
      return;
    }

    const stageIsValid = await trigger(stageValidationNames(stage, values));
    const documentsAreValid = validateStageDocuments(
      stage,
      values,
      setError,
      clearErrors,
    );
    const groupsAreValid = validateRequiredGroups(
      stage,
      values,
      setError,
      clearErrors,
    );

    if (
      !stageIsValid ||
      !documentsAreValid ||
      !groupsAreValid
    )
      return;

    await submitStage(values);
  };

  const handleFieldChange = (name) => {
    Promise.resolve().then(async () => {
      await trigger(name);

      const values = getValues();

      const requiredGroup = requiredGroupForField(
        stage,
        name,
      );

      if (requiredGroup) {
        validateRequiredGroup(
          requiredGroup,
          values,
          setError,
          clearErrors,
        );
      }

      customFieldChange?.({
        clearErrors,
        getPath,
        name,
        setValue,
        values,
      });
    });
  };

  return h(
    "div",
    { style: styles.shell },
    loading && h("p", { style: styles.paragraph }, "Loading..."),
    comment &&
      h(
        "div",
        { style: styles.commentBubble },
        h("p", { style: styles.commentText }, comment),
        h("small", { style: styles.commentDate }, commentedAt),
      ),
    h(
      "nav",
      { "aria-label": "KYC stages", style: styles.steps },
      visibleStages.map((item, index) => {
        const complete = item.submission
          ? readyToSign
          : stageIsComplete(item, values, serverPayload);
        const StepIcon = item.icon || (complete ? CheckCircleIcon : IssueDraftIcon);

        return h(
          "span",
          { key: item.title },
          // index > 0 && h("small", { style: styles.stepDelimiter }, "—"),
          h(
            "span",
            { style: styles.stepItem },
            h(StepIcon, {
              size: 16,
              style: styles.stepIcon,
            }),
            h(
              "small",
              {
                onClick: (event) => {
                  event.preventDefault();
                  setStageIndex(index);
                },
                style: {
                  ...styles.stepLink,
                  ...(index === stageIndex ? styles.activeStepLink : {}),
                },
              },
              item.title,
            ),
          ),
        );
      }),
    ),
    h(
      "form",
      { onSubmit: handleStageSubmit, style: styles.form },
      h("h3", { style: styles.title }, stage.title),
      stageIndex === 0 &&
        initHtml &&
        h("div", {
          dangerouslySetInnerHTML: { __html: initHtml },
          style: styles.initHtml,
          className: "intro"
        }),
      stage.paragraph && h("p", { style: styles.paragraph }, stage.paragraph),
      stage.submission &&
        (canSign
          ? h(
              React.Fragment,
              null,
              representations &&
                h("div", {
                  dangerouslySetInnerHTML: { __html: representations },
                  className: "intro"
                }),
              h(Field, {
                field: otpAttemptField,
                register,
                errors,
                onChange: handleFieldChange,
                values,
              }),
            )
          : h(
              "p",
              { style: styles.paragraph },
              canSignMessage,
            )),
      !stage.submission &&
        stage.fields.map((field) =>
        field.type === "array"
          ? h(ArrayField, {
              key: field.name,
              documentUrl,
              field,
              control,
              clearErrors,
              errors,
              onChange: handleFieldChange,
              register,
              setError,
              setValue,
              values,
            })
          : field.type === "documents"
            ? h(DocumentsField, {
                key: field.name,
                clearErrors,
                documentUrl,
                errors,
                field,
                register,
                setError,
                setValue,
                values,
              })
            : h(Field, {
                key: field.name,
                field,
                register,
                errors,
                onChange: handleFieldChange,
                values,
              }),
      ),
      submitError &&
        h(
          "div",
          { role: "alert", style: styles.errorPanel },
          h(AlertFillIcon, { size: 16, style: styles.errorIcon }),
          h("span", null, submitError),
        ),
      stage.submission &&
        canSign &&
        readyToSignMessages.map((message) =>
          h(
            "div",
            { key: message, role: "alert", style: styles.errorPanel },
            h(AlertFillIcon, { size: 16, style: styles.errorIcon }),
            h("span", null, message),
          ),
        ),
      (!stage.submission || canSign) &&
        h(
          "div",
          { style: styles.actions },
          h(
            "button",
            {
              type: "submit",
              disabled: submitting || (stage.submission && !readyToSign),
              style: styles.primaryButton,
            },
            submitting
              ? stage.submission
                ? "Submitting…"
                : "Saving…"
              : stage.submission
                ? "Submit"
                : "Save and continue",
          ),
        ),
    ),
  );
}

const styles = {
  shell: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr)",
    gap: "20px",
    maxWidth: "920px",
  },
  steps: {
    display: "flex",
    flexWrap: "wrap",
    gap: "6px",
  },
  stepDelimiter: {},
  completionMark: {},
  stepItem: {
    alignItems: "center",
    display: "inline-flex",
    gap: "3px",
    whiteSpace: "nowrap",
  },
  stepIcon: {
    display: "block",
    flex: "0 0 auto",
  },
  stepLink: {
    cursor: "pointer",
    textDecoration: "underline",
  },
  activeStepLink: {
    textDecoration: "none",
  },
  form: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr)",
    gap: "18px",
  },
  title: {
    margin: 0,
  },
  initHtml: {},
  paragraph: {
    margin: 0,
    lineHeight: 1.45,
  },
  commentBubble: {
    background: "var(--grid-bg-color)",
    borderRadius: "3px",
    lineHeight: 1.45,
    maxWidth: "760px",
    padding: "12px 14px",
    position: "relative",
  },
  commentDate: {
    display: "block",
    marginTop: "6px",
  },
  commentText: {
    margin: 0,
    whiteSpace: "pre-wrap",
  },
  field: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr)",
    gap: "5px",
    marginBottom: "10px",
  },
  label: {},
  help: {
    lineHeight: 1.4,
  },
  input: {},
  textarea: {
    boxSizing: "border-box",
    lineHeight: 1.35,
    minHeight: "calc(1.35em + 4px)",
    overflow: "hidden",
    resize: "none",
  },
  choiceInput: {
    flex: "0 0 16px",
    height: "16px",
    margin: "2px 0 0",
    width: "16px",
  },
  checkboxOption: {
    alignItems: "flex-start",
    display: "flex",
    gap: "8px",
    lineHeight: 1.4,
  },
  radioGroup: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr)",
    gap: "8px",
  },
  radioInput: {
    flex: "0 0 16px",
    height: "16px",
    margin: "6px 0 0",
    width: "16px",
  },
  radioOption: {
    alignItems: "flex-start",
    display: "flex",
    gap: "8px",
    lineHeight: 1.4,
  },
  arrayField: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr)",
    gap: "12px",
  },
  arrayAddButton: {
    justifySelf: "start",
    marginBottom: "16px",
  },
  arrayItem: {
    border: "1px solid var(--grid-bg-color)",
    borderRadius: "8px",
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr)",
    gap: "14px",
    position: "relative",
    padding: "14px",
  },
  arrayRemoveButton: {
    position: "absolute",
    right: "14px",
    top: "14px",
  },
  documentsList: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr)",
    gap: "8px",
    listStyle: "none",
    margin: "8px 0",
    padding: 0,
  },
  documentRemove: {
    cursor: "pointer",
    textDecoration: "underline",
    paddingLeft: "5px",
  },
  actions: {},
  primaryButton: {},
  secondaryButton: {
    background: "#fff",
    border: "1px solid #c9ced8",
    borderRadius: "6px",
    color: "#28313f",
    cursor: "pointer",
    font: "inherit",
    padding: "9px 12px",
  },
  error: {
    alignItems: "center",
    display: "flex",
    gap: "5px",
  },
  errorIcon: {
    display: "block",
    flex: "0 0 auto",
  },
  errorPanel: {
    alignItems: "center",
    // background: "#fff1f0",
    // border: "1px solid #f0b8b0",
    // borderRadius: "6px",
    // color: "#9f1f14",
    display: "flex",
    gap: "5px",
    margin: 0,
    // padding: "10px 12px",
  },
};

export function mountOnboardingForm(elementId, config) {
  const element = document.getElementById(elementId);
  const url = element?.getAttribute("data-url");
  const documentUrl = element?.getAttribute("data-document-url");

  if (!element || !url) return;

  stages = config.stages;
  requiredGroups = config.requiredGroups || [];
  booleanRadiobuttonFieldNames =
    config.booleanRadiobuttonFieldNames || new Set();
  customNormalizeFieldValue = config.normalizeFieldValue || null;
  customNormalizeFormPayload = config.normalizeFormPayload || null;
  customFieldChange = config.onFieldChange || null;

  createRoot(element).render(h(OnboardingForm, { documentUrl, url }));
}
